import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box} from 'views/components';
import {translate} from 'views/module';

import FlagGroups from './FlagGroups';
import MonitorHeader from './MonitorHeader';
import SourceStrip from './SourceStrip';
import TypeTable from './TypeTable';

import {
  buildFlagGroups,
  buildJobsByCaseRn,
  buildMarkedMetricDates,
  buildMonitorRows,
  buildTypeSummaries,
  caseQueryFields,
  countJobsWithoutCase,
  filterActiveSince,
  getNameableResourceRns,
  getOverdueCaseRns,
  getRangeStartTime,
  loadAllCasePages,
  mergeCasesByRn,
  monitorRanges,
} from './monitorUtils';
import {getParticipantKind} from '../communication/communicationUtils';
import backend from 'utils/backend';

const ALL_TYPES = 'ALL';
const PARTICIPANT_LOAD_BATCH_SIZE = 8;
const markingStatuses = ['MARKED_GOOD', 'MARKED_BAD', 'FLAGGED'];

const pickCaseFields = (caseData) => {
  return caseQueryFields.reduce((result, field) => {
    if (caseData[field] !== undefined) {
      result[field] = caseData[field];
    }

    return result;
  }, {});
};

const loadParticipantDetail = async (participantResourceRn) => {
  try {
    const isClient = getParticipantKind(participantResourceRn) === 'CLIENT';
    const output = isClient ?
      await backend.client.getClient(participantResourceRn) :
      await backend.caregiver.getCaregiver(participantResourceRn);

    return [participantResourceRn, {detail: isClient ? output?.clientDetail : output?.caregiverDetail, loadFailed: false}];
  } catch {
    return [participantResourceRn, {detail: null, loadFailed: true}];
  }
};

const CaseMonitor = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;

  const [rangeKey, setRangeKey] = React.useState('LAST_7_DAYS');
  const [now, setNow] = React.useState(() => Date.now());
  const [openPendingCases, setOpenPendingCases] = React.useState([]);
  const [openPendingSource, setOpenPendingSource] = React.useState({status: 'LOADING', pages: 0});
  const [jobs, setJobs] = React.useState([]);
  const [jobsSource, setJobsSource] = React.useState({status: 'LOADING'});
  const [loadMarked, setLoadMarked] = React.useState(false);
  const [markedCases, setMarkedCases] = React.useState([]);
  const [markedSource, setMarkedSource] = React.useState({status: 'IDLE', pages: 0});
  const [loadCompleted, setLoadCompleted] = React.useState(false);
  const [completedCases, setCompletedCases] = React.useState([]);
  const [completedSource, setCompletedSource] = React.useState({status: 'IDLE', pages: 0});
  const [fetchedCases, setFetchedCases] = React.useState([]);
  const [domainGuides, setDomainGuides] = React.useState([]);
  const [selectedCaseType, setSelectedCaseType] = React.useState(null);
  const [expandedFlagKeys, setExpandedFlagKeys] = React.useState(null);
  const [participantDetails, setParticipantDetails] = React.useState({});
  const fetchedCaseRnsRef = React.useRef(new Set());
  const participantLoadingRef = React.useRef(new Set());

  const selectedRange = React.useMemo(() => {
    return monitorRanges.find((range) => range.key === rangeKey) || monitorRanges[0];
  }, [rangeKey]);
  const jobsLoaded = jobsSource.status === 'LOADED';
  const marksLoaded = loadMarked && markedSource.status === 'LOADED';
  // Every completed case since launch is too large a load, so All time skips it.
  const loadCompletedDisabled = !selectedRange.days;
  const shouldLoadCompleted = loadCompleted && !loadCompletedDisabled;

  // Open and pending cases and the pending jobs do not depend on the range, so
  // they load once.
  React.useEffect(() => {
    let isActive = true;
    const isCurrent = () => isActive;

    const loadOpenPending = async () => {
      try {
        const result = await loadAllCasePages(
            backend.case.queryCase,
            [{key: 'status', value: ['OPEN', 'PENDING']}],
            isCurrent,
            (pages) => setOpenPendingSource({status: 'LOADING', pages}),
        );

        if (!result) return;

        setOpenPendingCases(result.cases);
        setOpenPendingSource({status: 'LOADED', pages: result.pages});
      } catch {
        if (!isActive) return;

        setOpenPendingSource((currentSource) => ({...currentSource, status: 'FAILED'}));
      }
    };

    const loadJobs = async () => {
      try {
        const response = await backend.agentJobs.getPendingJobs();

        if (!isActive) return;

        const pendingJobs = response?.jobs || [];
        setJobs(pendingJobs);
        setJobsSource({
          status: 'LOADED',
          jobCount: pendingJobs.length,
          skippedCount: countJobsWithoutCase(pendingJobs),
        });
      } catch {
        if (!isActive) return;

        setJobsSource({status: 'FAILED'});
      }
    };

    // Only failure category labels come from the guides; without them the raw
    // category key is shown.
    const loadDomainGuides = async () => {
      try {
        const response = await backend.domainGuide.listDomainGuides();

        if (!isActive) return;

        setDomainGuides(Array.isArray(response) ? response : response?.list || []);
      } catch {
        // Labels fall back to the category key.
      }
    };

    progressWrap(async () => {
      await Promise.all([loadOpenPending(), loadJobs(), loadDomainGuides()]);

      if (isActive) {
        setNow(Date.now());
      }
    }, {showProgress: true});

    return () => {
      isActive = false;
    };
  }, [progressWrap]);

  // Marks are counted by the day they were marked, which is the basis the Agent
  // Report Card uses. All time has no day list, so it filters on the mark itself.
  React.useEffect(() => {
    setMarkedCases([]);

    if (!loadMarked) {
      setMarkedSource({status: 'IDLE', pages: 0});
      return;
    }

    let isActive = true;
    const isCurrent = () => isActive;
    const filters = selectedRange.days ?
      [{key: 'markingMetricDate', value: buildMarkedMetricDates(selectedRange.days)}] :
      [{key: 'markingStatus', value: markingStatuses}];

    setMarkedSource({status: 'LOADING', pages: 0});

    progressWrap(async () => {
      try {
        const result = await loadAllCasePages(
            backend.case.queryCase,
            filters,
            isCurrent,
            (pages) => setMarkedSource({status: 'LOADING', pages}),
        );

        if (!result) return;

        setMarkedCases(result.cases);
        setMarkedSource({status: 'LOADED', pages: result.pages});
      } catch {
        if (!isActive) return;

        setMarkedSource((currentSource) => ({...currentSource, status: 'FAILED'}));
      }
    }, {showProgress: true});

    return () => {
      isActive = false;
    };
  }, [loadMarked, progressWrap, selectedRange]);

  // Completed cases are bounded by their last update. No completion time is
  // indexed, and a completed case's last update is normally its completion.
  React.useEffect(() => {
    setCompletedCases([]);

    if (!shouldLoadCompleted) {
      setCompletedSource({status: 'IDLE', pages: 0});
      return;
    }

    let isActive = true;
    const isCurrent = () => isActive;
    const rangeStart = new Date(getRangeStartTime(selectedRange, Date.now())).toISOString();
    const queryCompleted = (filters, sort, lastEvaluatedKey, fields) => {
      return backend.case.queryCaseFromDate(rangeStart, filters, sort, lastEvaluatedKey, fields, 'updatedAt');
    };

    setCompletedSource({status: 'LOADING', pages: 0});

    progressWrap(async () => {
      try {
        const result = await loadAllCasePages(
            queryCompleted,
            [{key: 'status', value: ['COMPLETE']}],
            isCurrent,
            (pages) => setCompletedSource({status: 'LOADING', pages}),
        );

        if (!result) return;

        setCompletedCases(result.cases);
        setCompletedSource({status: 'LOADED', pages: result.pages});
      } catch {
        if (!isActive) return;

        setCompletedSource((currentSource) => ({...currentSource, status: 'FAILED'}));
      }
    }, {showProgress: true});

    return () => {
      isActive = false;
    };
  }, [progressWrap, selectedRange, shouldLoadCompleted]);

  const jobsByCaseRn = React.useMemo(() => buildJobsByCaseRn(jobs), [jobs]);

  // A completed case can still have an overdue follow-up job. Only those cases
  // are fetched one by one; a job that is not late yet changes no count.
  React.useEffect(() => {
    if (!jobsLoaded || openPendingSource.status !== 'LOADED') return;

    const knownCaseRns = new Set([...openPendingCases, ...markedCases, ...completedCases, ...fetchedCases].map((caseData) => {
      return caseData.caseRn;
    }));
    const caseRnsToFetch = getOverdueCaseRns(jobsByCaseRn, now).filter((caseRn) => {
      return !knownCaseRns.has(caseRn) && !fetchedCaseRnsRef.current.has(caseRn);
    });

    if (!caseRnsToFetch.length) return;

    caseRnsToFetch.forEach((caseRn) => fetchedCaseRnsRef.current.add(caseRn));

    const loadOverdueCases = async () => {
      const caseDetails = await Promise.all(caseRnsToFetch.map(async (caseRn) => {
        try {
          return await backend.case.getCase(caseRn);
        } catch {
          return null;
        }
      }));

      setFetchedCases((currentCases) => {
        return mergeCasesByRn(currentCases, caseDetails.filter((caseDetail) => caseDetail?.caseRn).map(pickCaseFields));
      });
    };

    loadOverdueCases();
  }, [completedCases, fetchedCases, jobsByCaseRn, jobsLoaded, markedCases, now, openPendingCases, openPendingSource.status]);

  const domainGuidesByRn = React.useMemo(() => {
    return domainGuides.reduce((result, domainGuideData) => ({...result, [domainGuideData.domainGuideRn]: domainGuideData}), {});
  }, [domainGuides]);

  const rangeStartTime = React.useMemo(() => getRangeStartTime(selectedRange, now), [now, selectedRange]);

  const rows = React.useMemo(() => {
    // Every open and pending case is loaded, and the range keeps those with
    // activity in it. Cases fetched for an overdue job follow the same rule.
    const activeOpenPendingCases = filterActiveSince(openPendingCases, rangeStartTime);
    const activeFetchedCases = filterActiveSince(fetchedCases, rangeStartTime);
    // Later sets win, so the open and pending query has the last word on status.
    const cases = [completedCases, markedCases, activeOpenPendingCases].reduce(mergeCasesByRn, activeFetchedCases);
    const markedCaseRns = new Set(markedCases.map((caseData) => caseData.caseRn));
    const completedCaseRns = new Set(completedCases.map((caseData) => caseData.caseRn));

    return buildMonitorRows({cases, markedCaseRns, completedCaseRns, jobsByCaseRn, jobsLoaded, now});
  }, [completedCases, fetchedCases, jobsByCaseRn, jobsLoaded, markedCases, now, openPendingCases, rangeStartTime]);

  const {typeSummaries, total} = React.useMemo(() => buildTypeSummaries(rows, rangeStartTime), [rangeStartTime, rows]);

  const effectiveCaseType = React.useMemo(() => {
    if (selectedCaseType === ALL_TYPES || typeSummaries.some((summary) => summary.caseType === selectedCaseType)) {
      return selectedCaseType;
    }

    return typeSummaries[0]?.caseType ?? ALL_TYPES;
  }, [selectedCaseType, typeSummaries]);
  const selectedSummary = effectiveCaseType === ALL_TYPES ?
    total :
    typeSummaries.find((summary) => summary.caseType === effectiveCaseType) || total;

  const flagGroups = React.useMemo(() => {
    const selectedRows = effectiveCaseType === ALL_TYPES ? rows : rows.filter((row) => row.caseType === effectiveCaseType);

    return buildFlagGroups(selectedRows, {
      includePending: openPendingSource.status === 'LOADED' && jobsLoaded && (!loadMarked || marksLoaded),
    }).filter((flagGroup) => flagGroup.rows.length > 0);
  }, [effectiveCaseType, jobsLoaded, loadMarked, marksLoaded, openPendingSource.status, rows]);

  // Until the reader opens or closes a group, the first non-empty one is open.
  const effectiveExpandedFlagKeys = React.useMemo(() => {
    return expandedFlagKeys ?? (flagGroups[0] ? [flagGroups[0].flagKey] : []);
  }, [expandedFlagKeys, flagGroups]);

  const toggleFlagKey = React.useCallback((flagKey) => {
    setExpandedFlagKeys((currentFlagKeys) => {
      const flagKeys = currentFlagKeys ?? effectiveExpandedFlagKeys;

      return flagKeys.includes(flagKey) ?
        flagKeys.filter((currentFlagKey) => currentFlagKey !== flagKey) :
        [...flagKeys, flagKey];
    });
  }, [effectiveExpandedFlagKeys]);

  const selectCaseType = React.useCallback((caseType) => {
    setSelectedCaseType(caseType);
    setExpandedFlagKeys(null);
  }, []);

  // Names are looked up only for rows in open groups: each name is one call.
  const nameableResourceRns = React.useMemo(() => {
    const visibleRows = flagGroups
        .filter((flagGroup) => effectiveExpandedFlagKeys.includes(flagGroup.flagKey))
        .flatMap((flagGroup) => flagGroup.rows);

    return getNameableResourceRns(visibleRows);
  }, [effectiveExpandedFlagKeys, flagGroups]);

  React.useEffect(() => {
    const resourceRnsToLoad = nameableResourceRns.filter((participantResourceRn) => {
      return !participantLoadingRef.current.has(participantResourceRn);
    });

    if (!resourceRnsToLoad.length) return;

    resourceRnsToLoad.forEach((participantResourceRn) => participantLoadingRef.current.add(participantResourceRn));

    const loadParticipantDetails = async () => {
      for (let startIndex = 0; startIndex < resourceRnsToLoad.length; startIndex += PARTICIPANT_LOAD_BATCH_SIZE) {
        const batch = resourceRnsToLoad.slice(startIndex, startIndex + PARTICIPANT_LOAD_BATCH_SIZE);
        const batchDetails = await Promise.all(batch.map(loadParticipantDetail));

        setParticipantDetails((currentDetails) => ({...currentDetails, ...Object.fromEntries(batchDetails)}));
      }
    };

    loadParticipantDetails();
  }, [nameableResourceRns]);

  const windowLabel = translate(`case.Monitor.Window.${rangeKey}`);
  const summaryValues = {
    openPending: total.open + total.pending,
    stalled: total.stalled,
    markedBad: total.MARKED_BAD,
    marked: total.marked,
  };
  const headerSummaryText = openPendingSource.status === 'LOADED' ?
    translate(marksLoaded ? 'case.Monitor.Summary' : 'case.Monitor.SummaryWithoutMarks', summaryValues) :
    translate('case.Monitor.Sources.Loading');
  const detailSummaryValues = {
    openPending: selectedSummary.open + selectedSummary.pending,
    beforeWindow: selectedSummary.createdBeforeWindow,
    days: selectedRange.days,
    stalled: selectedSummary.stalled,
    markedBad: selectedSummary.MARKED_BAD,
    marked: selectedSummary.marked,
  };
  const detailSummaryKey = selectedRange.days ? 'Summary' : 'SummaryAllTime';
  const detailSummaryText = translate(
      `case.Monitor.Detail.${detailSummaryKey}${marksLoaded ? '' : 'WithoutMarks'}`,
      detailSummaryValues,
  );

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #ECECF1',
      }}
    >
      <MonitorHeader
        summaryText={headerSummaryText}
        ranges={monitorRanges}
        rangeKey={rangeKey}
        onRangeKeyChange={setRangeKey}
        loadMarked={loadMarked}
        onLoadMarkedChange={setLoadMarked}
        loadCompleted={loadCompleted}
        loadCompletedDisabled={loadCompletedDisabled}
        onLoadCompletedChange={setLoadCompleted}
      />
      <SourceStrip
        openPendingSource={openPendingSource}
        jobsSource={{...jobsSource, fetchedCount: fetchedCases.length}}
        markedSource={markedSource}
        completedSource={completedSource}
        rangeKey={rangeKey}
      />
      <TypeTable
        typeSummaries={typeSummaries}
        total={total}
        selectedCaseType={effectiveCaseType}
        showMarks={loadMarked}
        marksLoaded={marksLoaded}
        marksWindowLabel={windowLabel}
        showComplete={completedSource.status === 'LOADED'}
        onSelectCaseType={selectCaseType}
      />
      <FlagGroups
        title={effectiveCaseType === ALL_TYPES ? translate('case.Monitor.Table.AllTypes') : effectiveCaseType}
        summaryText={detailSummaryText}
        windowLabel={windowLabel}
        flagGroups={flagGroups}
        expandedFlagKeys={effectiveExpandedFlagKeys}
        onToggleFlagKey={toggleFlagKey}
        now={now}
        domainGuidesByRn={domainGuidesByRn}
        participantDetails={participantDetails}
      />
    </Box>
  );
};

CaseMonitor.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaseMonitor));
